import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, FileText, Car, Bike, X, ChevronDown, ChevronUp, Trophy, Table, Eye, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import XlsxPreviewModal from './XlsxPreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RaceResultsAdmin() {
  // Race Results State
  const [raceResults, setRaceResults] = useState([]);
  const [raceFormOpen, setRaceFormOpen] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  const [raceFormData, setRaceFormData] = useState({
    round_number: 1,
    race_name: '',
    race_date: '',
    league: 'racing',
    description: ''
  });

  // Championship Results State
  const [champResults, setChampResults] = useState([]);
  const [champFormOpen, setChampFormOpen] = useState(false);
  const [editingChamp, setEditingChamp] = useState(null);
  const [champFormData, setChampFormData] = useState({
    title: '',
    league: 'racing',
    description: ''
  });

  const [uploading, setUploading] = useState(null);
  const [expandedResult, setExpandedResult] = useState(null);
  const [previewXlsx, setPreviewXlsx] = useState(null);
  const [editingFileName, setEditingFileName] = useState(null); // {resultId, index, type: 'pdf'|'xlsx', name}

  const handleRenameFile = async (resultId, index, type, newName) => {
    if (!newName.trim()) return;
    try {
      const endpoint = type === 'pdf'
        ? `${BACKEND_URL}/api/admin/published-results/${resultId}/rename-pdf/${index}?new_name=${encodeURIComponent(newName.trim())}`
        : `${BACKEND_URL}/api/admin/championship-results/${resultId}/rename-xlsx/${index}?new_name=${encodeURIComponent(newName.trim())}`;
      await axios.put(endpoint, {}, { withCredentials: true });
      toast.success('File renamed');
      setEditingFileName(null);
      if (type === 'pdf') fetchRaceResults(); else fetchChampResults();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    }
  };

  useEffect(() => {
    fetchRaceResults();
    fetchChampResults();
  }, []);

  // ============== RACE RESULTS FUNCTIONS ==============
  const fetchRaceResults = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/published-results`, { withCredentials: true });
      setRaceResults(response.data);
    } catch (error) {
      console.error('Failed to fetch race results:', error);
    }
  };

  const handleRaceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRace) {
        await axios.put(`${BACKEND_URL}/api/admin/published-results/${editingRace.result_id}`, raceFormData, { withCredentials: true });
        toast.success('Race result updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/published-results`, raceFormData, { withCredentials: true });
        toast.success('Race result created');
      }
      setRaceFormOpen(false);
      setEditingRace(null);
      setRaceFormData({ round_number: 1, race_name: '', race_date: '', league: 'racing', description: '' });
      fetchRaceResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleRaceDelete = async (resultId) => {
    if (!window.confirm('Delete this race result and all its PDFs?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/published-results/${resultId}`, { withCredentials: true });
      toast.success('Race result deleted');
      fetchRaceResults();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePdfUpload = async (resultId, file) => {
    if (!file) return;
    const pdfName = window.prompt('Enter a name for this PDF:', file.name.replace('.pdf', ''));
    if (pdfName === null) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      setUploading(resultId);
      toast.loading('Uploading PDF...');
      await axios.post(
        `${BACKEND_URL}/api/admin/published-results/${resultId}/upload-pdf?pdf_name=${encodeURIComponent(pdfName || file.name)}`,
        formDataUpload,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.dismiss();
      toast.success('PDF uploaded!');
      fetchRaceResults();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(null);
    }
  };

  const handlePdfDelete = async (resultId, pdfIndex, pdfName) => {
    if (!window.confirm(`Delete "${pdfName}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/published-results/${resultId}/pdf/${pdfIndex}`, { withCredentials: true });
      toast.success('PDF deleted');
      fetchRaceResults();
    } catch (error) {
      toast.error('Failed to delete PDF');
    }
  };

  // ============== CHAMPIONSHIP RESULTS FUNCTIONS ==============
  const fetchChampResults = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/championship-results`, { withCredentials: true });
      setChampResults(response.data);
    } catch (error) {
      console.error('Failed to fetch championship results:', error);
    }
  };

  const handleChampSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingChamp) {
        await axios.put(`${BACKEND_URL}/api/admin/championship-results/${editingChamp.result_id}`, champFormData, { withCredentials: true });
        toast.success('Championship result updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/championship-results`, champFormData, { withCredentials: true });
        toast.success('Championship result created');
      }
      setChampFormOpen(false);
      setEditingChamp(null);
      setChampFormData({ title: '', league: 'racing', description: '' });
      fetchChampResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleChampDelete = async (resultId) => {
    if (!window.confirm('Delete this championship result and all its files?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/championship-results/${resultId}`, { withCredentials: true });
      toast.success('Championship result deleted');
      fetchChampResults();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleXlsxUpload = async (resultId, file) => {
    if (!file) return;
    const xlsxName = window.prompt('Enter a name for this file:', file.name.replace(/\.(xlsx|xls)$/i, ''));
    if (xlsxName === null) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      setUploading(resultId);
      toast.loading('Uploading XLSX...');
      await axios.post(
        `${BACKEND_URL}/api/admin/championship-results/${resultId}/upload-xlsx?xlsx_name=${encodeURIComponent(xlsxName || file.name)}`,
        formDataUpload,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.dismiss();
      toast.success('XLSX uploaded!');
      fetchChampResults();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to upload XLSX');
    } finally {
      setUploading(null);
    }
  };

  const handleXlsxDelete = async (resultId, xlsxIndex, xlsxName) => {
    if (!window.confirm(`Delete "${xlsxName}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/championship-results/${resultId}/xlsx/${xlsxIndex}`, { withCredentials: true });
      toast.success('XLSX deleted');
      fetchChampResults();
    } catch (error) {
      toast.error('Failed to delete XLSX');
    }
  };

  // ============== HELPER FUNCTIONS ==============
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getPdfs = (result) => {
    if (result.pdf_files && result.pdf_files.length > 0) return result.pdf_files;
    if (result.pdf_url) return [{ name: 'Results PDF', url: result.pdf_url }];
    return [];
  };

  // Group results by league
  const racingRaceResults = raceResults.filter(r => r.league === 'racing').sort((a, b) => a.round_number - b.round_number);
  const motoRaceResults = raceResults.filter(r => r.league === 'moto').sort((a, b) => a.round_number - b.round_number);
  const racingChampResults = champResults.filter(r => r.league === 'racing');
  const motoChampResults = champResults.filter(r => r.league === 'moto');

  // ============== RENDER COMPONENTS ==============
  const RaceResultCard = ({ result, accentColor }) => {
    const pdfs = getPdfs(result);
    const isExpanded = expandedResult === result.result_id;
    
    return (
      <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
          onClick={() => setExpandedResult(isExpanded ? null : result.result_id)}
        >
          <div className="flex items-center space-x-4">
            <span className={`w-10 h-10 bg-${accentColor} flex items-center justify-center font-heading font-bold ${accentColor === 'secondary' ? 'text-black' : 'text-white'}`}>
              R{result.round_number}
            </span>
            <div>
              <p className="font-bold">{result.race_name}</p>
              <p className="text-sm text-zinc-400">{formatDate(result.race_date)}</p>
            </div>
            <span className={`px-2 py-1 text-xs font-bold bg-${accentColor}/20 text-${accentColor}`}>
              {pdfs.length} PDF{pdfs.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <label className={`flex items-center space-x-2 px-3 py-2 cursor-pointer ${uploading === result.result_id ? 'bg-zinc-700' : 'bg-zinc-800 hover:bg-zinc-700'} transition-colors`} onClick={(e) => e.stopPropagation()}>
              <Upload size={16} />
              <span className="text-sm">Add PDF</span>
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => handlePdfUpload(result.result_id, e.target.files[0])} disabled={uploading === result.result_id} />
            </label>
            <button onClick={(e) => { e.stopPropagation(); setEditingRace(result); setRaceFormData({ round_number: result.round_number, race_name: result.race_name, race_date: result.race_date, league: result.league, description: result.description || '' }); setRaceFormOpen(true); }} className="p-2 text-secondary hover:text-white">
              <Edit size={18} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleRaceDelete(result.result_id); }} className="p-2 text-red-500 hover:text-red-400">
              <Trash2 size={18} />
            </button>
            {isExpanded ? <ChevronUp size={20} className="text-zinc-500" /> : <ChevronDown size={20} className="text-zinc-500" />}
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-zinc-800 p-4 bg-zinc-950">
            {result.description && <p className="text-sm text-zinc-400 mb-4">{result.description}</p>}
            <div className="space-y-2">
              <p className="text-sm font-medium text-zinc-500 mb-2">Uploaded PDFs:</p>
              {pdfs.length === 0 ? (
                <p className="text-sm text-zinc-500 italic">No PDFs uploaded yet</p>
              ) : (
                pdfs.map((pdf, index) => (
                  <div key={pdf.url || `${result.result_id}-pdf-${index}`} className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <FileText size={18} className={`text-${accentColor} shrink-0`} />
                      {editingFileName?.resultId === result.result_id && editingFileName?.index === index && editingFileName?.type === 'pdf' ? (
                        <input
                          type="text"
                          value={editingFileName.name}
                          onChange={(e) => setEditingFileName({ ...editingFileName, name: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && handleRenameFile(result.result_id, index, 'pdf', editingFileName.name)}
                          onBlur={() => setEditingFileName(null)}
                          className="bg-zinc-800 border border-zinc-700 px-2 py-0.5 text-sm text-white focus:outline-none flex-1"
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm font-medium truncate">{pdf.name}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 shrink-0">
                      <button onClick={() => setEditingFileName({ resultId: result.result_id, index, type: 'pdf', name: pdf.name })} className="p-1 text-zinc-500 hover:text-white" title="Rename"><Pencil size={14} /></button>
                      <a href={`${BACKEND_URL}${pdf.url}`} target="_blank" rel="noopener noreferrer" className={`text-sm text-${accentColor} hover:underline`}>View</a>
                      <button onClick={() => handlePdfDelete(result.result_id, index, pdf.name)} className="p-1 text-red-500 hover:text-red-400"><X size={16} /></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const ChampResultCard = ({ result, accentColor }) => {
    const xlsxFiles = result.xlsx_files || [];
    const isExpanded = expandedResult === `champ_${result.result_id}`;
    
    return (
      <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
          onClick={() => setExpandedResult(isExpanded ? null : `champ_${result.result_id}`)}
        >
          <div className="flex items-center space-x-4">
            <span className={`w-10 h-10 bg-${accentColor} flex items-center justify-center ${accentColor === 'secondary' ? 'text-black' : 'text-white'}`}>
              <Trophy size={20} />
            </span>
            <div>
              <p className="font-bold">{result.title}</p>
              <p className="text-sm text-zinc-400">{result.league === 'racing' ? 'MSF Racing' : 'MSF Moto'}</p>
            </div>
            <span className={`px-2 py-1 text-xs font-bold bg-green-500/20 text-green-500`}>
              {xlsxFiles.length} XLSX{xlsxFiles.length !== 1 ? '' : ''}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <label className={`flex items-center space-x-2 px-3 py-2 cursor-pointer ${uploading === result.result_id ? 'bg-zinc-700' : 'bg-green-700 hover:bg-green-600'} transition-colors`} onClick={(e) => e.stopPropagation()}>
              <Upload size={16} />
              <span className="text-sm">Add XLSX</span>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleXlsxUpload(result.result_id, e.target.files[0])} disabled={uploading === result.result_id} />
            </label>
            <button onClick={(e) => { e.stopPropagation(); setEditingChamp(result); setChampFormData({ title: result.title, league: result.league, description: result.description || '' }); setChampFormOpen(true); }} className="p-2 text-secondary hover:text-white">
              <Edit size={18} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleChampDelete(result.result_id); }} className="p-2 text-red-500 hover:text-red-400">
              <Trash2 size={18} />
            </button>
            {isExpanded ? <ChevronUp size={20} className="text-zinc-500" /> : <ChevronDown size={20} className="text-zinc-500" />}
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-zinc-800 p-4 bg-zinc-950">
            {result.description && <p className="text-sm text-zinc-400 mb-4">{result.description}</p>}
            <div className="space-y-2">
              <p className="text-sm font-medium text-zinc-500 mb-2">Uploaded XLSX Files:</p>
              {xlsxFiles.length === 0 ? (
                <p className="text-sm text-zinc-500 italic">No XLSX files uploaded yet</p>
              ) : (
                xlsxFiles.map((xlsx, index) => (
                  <div key={xlsx.url || `${result.result_id}-xlsx-${index}`} className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <Table size={18} className="text-green-500 shrink-0" />
                      {editingFileName?.resultId === result.result_id && editingFileName?.index === index && editingFileName?.type === 'xlsx' ? (
                        <input
                          type="text"
                          value={editingFileName.name}
                          onChange={(e) => setEditingFileName({ ...editingFileName, name: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && handleRenameFile(result.result_id, index, 'xlsx', editingFileName.name)}
                          onBlur={() => setEditingFileName(null)}
                          className="bg-zinc-800 border border-zinc-700 px-2 py-0.5 text-sm text-white focus:outline-none flex-1"
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm font-medium truncate">{xlsx.name}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 shrink-0">
                      <button onClick={() => setEditingFileName({ resultId: result.result_id, index, type: 'xlsx', name: xlsx.name })} className="p-1 text-zinc-500 hover:text-white" title="Rename"><Pencil size={14} /></button>
                      <button onClick={() => setPreviewXlsx({ url: xlsx.url, name: xlsx.name })} className="text-sm text-green-500 hover:underline flex items-center gap-1" data-testid={`admin-xlsx-view-${index}`}><Eye size={14} /> View</button>
                      <a href={`${BACKEND_URL}${xlsx.url}`} download className="text-sm text-zinc-400 hover:underline">Download</a>
                      <button onClick={() => handleXlsxDelete(result.result_id, index, xlsx.name)} className="p-1 text-red-500 hover:text-red-400"><X size={16} /></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* ============== RACE RESULTS SECTION ============== */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center">
              <FileText size={24} className="mr-2 text-primary" />
              Race Results (PDFs)
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Upload race results for each round</p>
          </div>
          <Dialog open={raceFormOpen} onOpenChange={setRaceFormOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setEditingRace(null); setRaceFormData({ round_number: 1, race_name: '', race_date: '', league: 'racing', description: '' }); }} className="btn-skew bg-primary text-white px-6 py-2 font-bold uppercase flex items-center space-x-2" data-testid="add-race-result-button">
                <Plus size={18} />
                <span>Add Race Result</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border border-zinc-800 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-2xl">{editingRace ? 'Edit Race Result' : 'Add Race Result'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleRaceSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Round Number</label>
                    <input type="number" value={raceFormData.round_number} onChange={(e) => setRaceFormData({ ...raceFormData, round_number: parseInt(e.target.value) })} min="1" className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">League</label>
                    <select value={raceFormData.league} onChange={(e) => setRaceFormData({ ...raceFormData, league: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white">
                      <option value="racing">MSF Racing</option>
                      <option value="moto">MSF Moto</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Race Name</label>
                  <input type="text" value={raceFormData.race_name} onChange={(e) => setRaceFormData({ ...raceFormData, race_name: e.target.value })} placeholder="e.g., Sepang Round 1" className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Race Date</label>
                  <input type="date" value={raceFormData.race_date} onChange={(e) => setRaceFormData({ ...raceFormData, race_date: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Description (optional)</label>
                  <textarea value={raceFormData.description} onChange={(e) => setRaceFormData({ ...raceFormData, description: e.target.value })} placeholder="Race highlights..." className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 text-white min-h-[80px]" />
                </div>
                <button type="submit" className="w-full bg-primary text-white py-3 font-bold uppercase">{editingRace ? 'Update' : 'Create'}</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Racing */}
          <div className="bg-card border border-zinc-800 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Car size={20} className="text-primary" />
              <h3 className="font-heading font-bold text-lg text-primary">MSF Racing</h3>
            </div>
            {racingRaceResults.length === 0 ? (
              <p className="text-zinc-500 italic text-sm">No racing results yet</p>
            ) : (
              <div className="space-y-3">{racingRaceResults.map((r) => <RaceResultCard key={r.result_id} result={r} accentColor="primary" />)}</div>
            )}
          </div>
          {/* Moto */}
          <div className="bg-card border border-zinc-800 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Bike size={20} className="text-secondary" />
              <h3 className="font-heading font-bold text-lg text-secondary">MSF Moto</h3>
            </div>
            {motoRaceResults.length === 0 ? (
              <p className="text-zinc-500 italic text-sm">No moto results yet</p>
            ) : (
              <div className="space-y-3">{motoRaceResults.map((r) => <RaceResultCard key={r.result_id} result={r} accentColor="secondary" />)}</div>
            )}
          </div>
        </div>
      </div>

      {/* ============== CHAMPIONSHIP RESULTS SECTION ============== */}
      <div className="border-t border-zinc-800 pt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center">
              <Trophy size={24} className="mr-2 text-green-500" />
              Championship Standings (XLSX)
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Upload championship standings spreadsheets</p>
          </div>
          <Dialog open={champFormOpen} onOpenChange={setChampFormOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setEditingChamp(null); setChampFormData({ title: '', league: 'racing', description: '' }); }} className="btn-skew bg-green-600 text-white px-6 py-2 font-bold uppercase flex items-center space-x-2" data-testid="add-champ-result-button">
                <Plus size={18} />
                <span>Add Championship</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border border-zinc-800 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-2xl">{editingChamp ? 'Edit Championship' : 'Add Championship'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleChampSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Title</label>
                  <input type="text" value={champFormData.title} onChange={(e) => setChampFormData({ ...champFormData, title: e.target.value })} placeholder="e.g., 2025 Season Championship Standings" className="w-full bg-zinc-950 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 h-12 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">League</label>
                  <select value={champFormData.league} onChange={(e) => setChampFormData({ ...champFormData, league: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 h-12 px-4 text-white">
                    <option value="racing">MSF Racing</option>
                    <option value="moto">MSF Moto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Description (optional)</label>
                  <textarea value={champFormData.description} onChange={(e) => setChampFormData({ ...champFormData, description: e.target.value })} placeholder="Additional details..." className="w-full bg-zinc-950 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 px-4 py-3 text-white min-h-[80px]" />
                </div>
                <button type="submit" className="w-full bg-green-600 text-white py-3 font-bold uppercase">{editingChamp ? 'Update' : 'Create'}</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Racing */}
          <div className="bg-card border border-zinc-800 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Car size={20} className="text-primary" />
              <h3 className="font-heading font-bold text-lg text-primary">MSF Racing</h3>
            </div>
            {racingChampResults.length === 0 ? (
              <p className="text-zinc-500 italic text-sm">No championship standings yet</p>
            ) : (
              <div className="space-y-3">{racingChampResults.map((r) => <ChampResultCard key={r.result_id} result={r} accentColor="primary" />)}</div>
            )}
          </div>
          {/* Moto */}
          <div className="bg-card border border-zinc-800 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Bike size={20} className="text-secondary" />
              <h3 className="font-heading font-bold text-lg text-secondary">MSF Moto</h3>
            </div>
            {motoChampResults.length === 0 ? (
              <p className="text-zinc-500 italic text-sm">No championship standings yet</p>
            ) : (
              <div className="space-y-3">{motoChampResults.map((r) => <ChampResultCard key={r.result_id} result={r} accentColor="secondary" />)}</div>
            )}
          </div>
        </div>
      </div>

      {/* XLSX Preview Modal */}
      <XlsxPreviewModal
        open={!!previewXlsx}
        onClose={() => setPreviewXlsx(null)}
        xlsxUrl={previewXlsx?.url}
        xlsxName={previewXlsx?.name}
      />
    </div>
  );
}
